import { useState } from 'react';
import { C, glass, ink } from '../lib/theme';
import { splitContacts } from '../lib/format';
import type { LostEvent } from '../lib/types';
import { IconPhone } from './icons';

/**
 * Estados do semáforo (PRD §9-B — a distinção é obrigatória):
 *  - `ok`      verde  → há contato sincronizado
 *  - `none`    vermelho→ SABEMOS que não há contato (campo veio vazio)
 *  - `pending` cinza  → AINDA NÃO SABEMOS (sync não chegou; campo nulo)
 * Nunca vermelho pra "não sincronizado" — é a confusão que o PRD proíbe.
 */
export type SemaphoreState = 'ok' | 'none' | 'pending';

export function semaphoreOf(ev: Pick<LostEvent, 'email' | 'telefone'>): {
  state: SemaphoreState; lines: string[]; count: number;
} {
  const synced = ev.email !== null || ev.telefone !== null;
  const lines = [...splitContacts(ev.telefone), ...splitContacts(ev.email)];
  if (!synced) return { state: 'pending', lines: [], count: 0 };
  if (lines.length === 0) return { state: 'none', lines: [], count: 0 };
  return { state: 'ok', lines, count: lines.length };
}

const TONE: Record<SemaphoreState, { bg: string; border: string; color: string }> = {
  ok: { bg: 'rgba(46,204,113,0.13)', border: 'rgba(46,204,113,0.4)', color: C.green },
  none: { bg: 'rgba(242,85,90,0.13)', border: 'rgba(242,85,90,0.42)', color: C.churn },
  pending: { bg: ink(0.06), border: ink(0.16), color: ink(0.42) },
};

/**
 * Contact Semaphore (PRD §6). O contador é SEMPRE visível — o hover (nível 1)
 * é uma camada a mais, nunca a única forma de acessar o dado; o clique (nível 2)
 * abre o Modal de Contato completo.
 */
export function ContactSemaphore({ ev, onOpen }: { ev: LostEvent; onOpen: (e: React.MouseEvent) => void }) {
  const [hover, setHover] = useState(false);
  const sem = semaphoreOf(ev);
  const tone = TONE[sem.state];

  const title =
    sem.state === 'ok' ? `${sem.count} contato${sem.count === 1 ? '' : 's'} sincronizado${sem.count === 1 ? '' : 's'}`
    : sem.state === 'none' ? 'Sem contato cadastrado'
    : 'Dado ainda não sincronizado';

  const popTitle =
    sem.state === 'ok' ? `Contatos · ${sem.count}`
    : sem.state === 'none' ? 'Nenhum contato'
    : 'Aguardando sync';

  const popLines =
    sem.state === 'ok' ? sem.lines.slice(0, 2)
    : sem.state === 'none' ? ['Nenhum telefone ou email em revops_client_contacts_v.']
    : ['Cliente novo — o sync de stripe_customers_raw ainda não trouxe os contatos.'];

  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        type="button" onClick={onOpen} title={title}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', font: 'inherit',
          border: `1px solid ${tone.border}`, background: tone.bg, color: tone.color,
          padding: '7px 12px', borderRadius: 11, fontSize: 13, fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          boxShadow: sem.state === 'ok' ? '0 0 13px -4px rgba(46,204,113,0.85)' : 'none',
        }}
      >
        <IconPhone size={14} />
        {sem.state === 'pending' ? '–' : sem.count}
      </button>

      {hover && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 9px)', right: -6, zIndex: 70, width: 232,
          padding: '13px 15px', borderRadius: 14, ...glass, boxShadow: '0 22px 60px rgba(10,10,18,0.6)',
          textAlign: 'left', animation: 'koSlideIn 0.18s ease',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: C.accent, marginBottom: 8,
          }}>{popTitle}</div>
          {popLines.map((t, i) => (
            <div key={i} style={{
              fontSize: 12.5, color: ink(0.82), fontVariantNumeric: 'tabular-nums',
              marginBottom: i === popLines.length - 1 ? 0 : 3, wordBreak: 'break-all',
            }}>{t}</div>
          ))}
        </div>
      )}
    </div>
  );
}
